<template>
  <div></div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, onUnmounted, shallowRef } from 'vue'
import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import type { DevParam } from './types'

interface Props {
  devParam?: DevParam | null
}

const props = withDefaults(defineProps<Props>(), {
  devParam: null,
})

const z = computed(() => (props.devParam && props.devParam?.z_cm / 100 + 0.03) || 0)
// 使用shallowRef避免Vue深度代理Three.js对象
const scene = shallowRef<THREE.Group | null>(null)
const edges = shallowRef<THREE.Group | null>(null)
const loader = ref<GLTFLoader | null>(null)

// 导出scene和edges，供父组件使用
defineExpose({
  scene,
  edges,
})

// 加载模型 - 从服务器加载GLTF模型
const loadModel = () => {
  if (!loader.value) {
    loader.value = new GLTFLoader()
  }

  // 从public/models目录加载模型
  loader.value.load(
    '/models/radar_1.glb',
    (gltf) => {
      // 获取模型场景
      const modelScene = gltf.scene.clone()

      // 为模型应用材质
      modelScene.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          child.material = new THREE.MeshStandardMaterial({
            color: 0xffffff,
            metalness: 0.7,
            roughness: 0.2,
            emissive: 0xffffff,
            emissiveIntensity: 0.05,
          })
        }
      })

      // 设置模型缩放，与React版本保持一致
      modelScene.scale.set(0.05, 0.05, 0.05)
      // 设置模型位置
      modelScene.position.set(0, 0, z.value)

      scene.value = modelScene

      // 创建边缘线，与React版本保持一致
      const edgesGroup = new THREE.Group()
      modelScene.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          const edgeGeo = new THREE.EdgesGeometry(child.geometry, 20)
          const edgeMat = new THREE.LineBasicMaterial({
            color: 0xaaaaaa,
            transparent: true,
            opacity: 0.6,
          })
          edgesGroup.add(new THREE.LineSegments(edgeGeo, edgeMat))
        }
      })

      // 设置边缘线缩放和位置
      edgesGroup.scale.set(0.05, 0.05, 0.05)
      edgesGroup.position.set(0, 0, z.value)

      edges.value = edgesGroup
    },
    undefined,
    (error) => {
      console.error('Error loading radar model:', error)
      // 加载失败时使用备用模型
      createFallbackModel()
    }
  )
}

// 创建简单的替代模型，当GLTF加载失败时使用
const createFallbackModel = () => {
  // 创建简单的立方体作为备用模型
  const fallbackScene = new THREE.Group()

  const geometry = new THREE.BoxGeometry(0.5, 0.5, 0.1)
  const material = new THREE.MeshStandardMaterial({
    color: 0xffffff,
    metalness: 0.7,
    roughness: 0.2,
    emissive: 0xffffff,
    emissiveIntensity: 0.05,
  })

  const mesh = new THREE.Mesh(geometry, material)
  mesh.rotation.x = Math.PI / 2
  fallbackScene.add(mesh)

  // 创建边缘线
  const edgesGeometry = new THREE.EdgesGeometry(geometry, 20)
  const edgesMaterial = new THREE.LineBasicMaterial({
    color: 0xaaaaaa,
    transparent: true,
    opacity: 0.6,
  })

  const edges = new THREE.LineSegments(edgesGeometry, edgesMaterial)
  edges.rotation.x = Math.PI / 2
  fallbackScene.add(edges)

  // 设置模型缩放和位置
  fallbackScene.scale.set(0.05, 0.05, 0.05)
  fallbackScene.position.set(0, 0, z.value)

  scene.value = fallbackScene
  edges.value = fallbackScene
}

onMounted(() => {
  loadModel()
})

onUnmounted(() => {
  // 清理资源
  if (scene.value) {
    scene.value.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.geometry.dispose()
        if (Array.isArray(child.material)) {
          child.material.forEach((material) => material.dispose())
        } else {
          child.material.dispose()
        }
      }
    })
  }
  if (edges.value) {
    edges.value.traverse((child) => {
      if (child instanceof THREE.LineSegments) {
        child.geometry.dispose()
        child.material.dispose()
      }
    })
  }
})
</script>
